import type { LatestEarnings } from "@/domain/gems-game/domain";
import { Box, Video } from "@biom3/react";
import { useRive, useStateMachineInput } from "@rive-app/react-canvas";
import { Layout } from "@rive-app/react-canvas";
import { Fit } from "@rive-app/react-canvas";
import { useEffect, useMemo, useState } from "react";

import { appConfig } from "@/constants";

const excitedRotatingGem = `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/videos/immutable/rotating-gem--excited.mp4`;
const standardRotatingGem = `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/videos/immutable/rotating-gem--standard.mp4`;

type GemsAnimationProps = {
  hasJustEarned: boolean;
  canClaim: boolean;
  eligibleGems: number;
  walletAddress: string | null;
  latestEarnings: LatestEarnings | undefined;
};

const VIDEO_FADE_DURATION = 3000;
const SPINNING_STOP_DELAY = 2000;

function NumberAnimation({
  direction = 1,
  number,
}: {
  direction?: -1 | 0 | 1;
  number?: number;
}) {
  const { RiveComponent, rive } = useRive({
    src: "/rive/counter-fade.riv",
    autoplay: true,
    stateMachines: "State Machine",
    layout: new Layout({ fit: Fit.Contain }),
  });

  const directionInput = useStateMachineInput(
    rive,
    "State Machine",
    "direction",
    direction,
  );

  if (directionInput) {
    directionInput.value = direction;
  }

  useStateMachineInput(rive, "State Machine", "number", number);
  return <RiveComponent height={240} width={80} />;
}

export function GemsAnimation({
  hasJustEarned,
  canClaim,
  eligibleGems,
  walletAddress,
  latestEarnings,
}: GemsAnimationProps) {
  const [isSpinning, setIsSpinning] = useState(true);
  const [fadeToExcited, setFadeToExcited] = useState(false);

  const [leftNumber, rightNumber] = useMemo(() => {
    if (!eligibleGems) return [0, 0];
    const digits = String(eligibleGems).padStart(2, "0");
    return [Number(digits[0]), Number(digits[1])];
  }, [eligibleGems]);

  useEffect(() => {
    if (canClaim) {
      setIsSpinning(true);
    }
  }, [canClaim]);

  useEffect(() => {
    if (hasJustEarned) {
      setFadeToExcited(true);
      setTimeout(() => {
        setIsSpinning(false);
      }, SPINNING_STOP_DELAY);
    } else {
      setFadeToExcited(false);
    }
  }, [hasJustEarned]);

  return (
    <>
      {hasJustEarned || canClaim || !walletAddress || !latestEarnings ? (
        <Box
          sx={{
            display: "flex",
            height: ["120px", "120px", "240px"],
            width: ["120px", "150px", "160px"],
            mr: "base.spacing.x3",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
          }}
        >
          <NumberAnimation direction={isSpinning ? 1 : 0} number={leftNumber} />
          <NumberAnimation
            direction={isSpinning ? -1 : 0}
            number={rightNumber}
          />
        </Box>
      ) : null}
      <Box
        sx={{
          mr: ["0", "base.spacing.x4", "base.spacing.x8"],
          position: "relative",
        }}
      >
        <Video
          sx={{
            width: ["75px", "75px", "200px"],
            position: "absolute",
            opacity: fadeToExcited ? 0 : 1,
            transition: `opacity ${VIDEO_FADE_DURATION}ms ease-in-out`,
          }}
          mimeType="video/mp4"
          videoUrl={standardRotatingGem}
          autoPlay
          preload="auto"
          loop
          muted
          controls={false}
        />
        <Video
          sx={{
            width: ["75px", "75px", "200px"],
            opacity: fadeToExcited ? 1 : 0,
            transition: `opacity ${VIDEO_FADE_DURATION}ms ease-in-out`,
          }}
          mimeType="video/mp4"
          videoUrl={excitedRotatingGem}
          autoPlay
          preload="auto"
          loop
          muted
          controls={false}
        />
      </Box>
    </>
  );
}
